import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import {
  Box,
  Checkbox,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography
} from "@mui/material";
import React, { useMemo, useState } from "react";

import { UserLicenseStatus } from "../../../../data/queries/userLicense";
import {
  LICENSE_ACTIVE_TOOLTIP,
  LICENSE_UNAVAILABLE_MESSAGE,
  LICENSE_WARNING_MESSAGE
} from "../../constants/license";
import { AnalysisEntity, AnalysisProfile } from "../../types";

interface EntityTableProps {
  entities: AnalysisEntity[];
  profiles: AnalysisProfile[];
  selectedIds: string[];
  showLicenseColumn?: boolean;
  licensesByEmail?: Record<string, UserLicenseStatus>;
  licensesLoading?: boolean;
  licensesFailed?: boolean;
  onSelectAll: (checked: boolean) => void;
  onSelectEntity: (id: string) => void;
  onEditEntity: (entity: AnalysisEntity) => void;
  onDeleteEntity: (id: string) => void;
}

type SortableColumn = "email" | "analysis_group" | "status" | "license" | "modified";
type SortDirection = "asc" | "desc";

const LICENSE_SORT_VALUE = {
  LICENSED: "0-licensed",
  UNLICENSED: "1-unlicensed",
  UNKNOWN: "2-unknown",
  LOADING: "3-loading"
} as const;

const getLicenseSortValue = (
  email: string,
  licensesByEmail: Record<string, UserLicenseStatus> | undefined,
  loading: boolean
): string => {
  const status = licensesByEmail?.[email.toLowerCase()];
  if (!status) {
    return loading ? LICENSE_SORT_VALUE.LOADING : LICENSE_SORT_VALUE.UNKNOWN;
  }
  return status.hasEnlightenPro ? LICENSE_SORT_VALUE.LICENSED : LICENSE_SORT_VALUE.UNLICENSED;
};

interface LicenseCellProps {
  email: string;
  licensesByEmail?: Record<string, UserLicenseStatus>;
  licensesLoading?: boolean;
  licensesFailed?: boolean;
}

const LicenseCell: React.FC<LicenseCellProps> = ({
  email,
  licensesByEmail,
  licensesLoading,
  licensesFailed
}) => {
  if (licensesFailed) {
    return (
      <Tooltip title={LICENSE_UNAVAILABLE_MESSAGE}>
        <WarningAmberIcon color="warning" fontSize="small" aria-label="License unverified" />
      </Tooltip>
    );
  }

  const status = licensesByEmail?.[email.toLowerCase()];

  if (!status && licensesLoading) {
    return <CircularProgress size={16} aria-label="Checking license" />;
  }

  if (!status) {
    return (
      <Tooltip title={LICENSE_UNAVAILABLE_MESSAGE}>
        <WarningAmberIcon color="warning" fontSize="small" aria-label="License unverified" />
      </Tooltip>
    );
  }

  if (status.hasEnlightenPro) {
    return (
      <Tooltip title={LICENSE_ACTIVE_TOOLTIP}>
        <CheckCircleIcon color="success" fontSize="small" aria-label="Licensed" />
      </Tooltip>
    );
  }

  return (
    <Tooltip title={LICENSE_WARNING_MESSAGE}>
      <WarningAmberIcon color="warning" fontSize="small" aria-label="License missing" />
    </Tooltip>
  );
};

export const EntityTable: React.FC<EntityTableProps> = ({
  entities,
  profiles,
  selectedIds,
  showLicenseColumn = true,
  licensesByEmail,
  licensesLoading,
  licensesFailed,
  onSelectAll,
  onSelectEntity,
  onEditEntity,
  onDeleteEntity
}) => {
  const [sortBy, setSortBy] = useState<SortableColumn>("modified");
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");

  const getProfileName = (profileId: string | null) => {
    if (!profileId) {
      return null;
    }
    const profile = profiles.find((p) => p.id === profileId);
    return profile?.name;
  };

  const handleSort = (column: SortableColumn) => {
    const isAsc = sortBy === column && sortDirection === "asc";
    setSortDirection(isAsc ? "desc" : "asc");
    setSortBy(column);
  };

  const sortValueGetters: Record<SortableColumn, (entity: AnalysisEntity) => string> = {
    email: (e) => e.email,
    analysis_group: (e) => getProfileName(e.analysis_profile_id) || "",
    status: (e) => e.status,
    license: (e) => getLicenseSortValue(e.email, licensesByEmail, Boolean(licensesLoading)),
    modified: (e) => e.modified
  };

  const sortedEntities = useMemo(() => {
    return [...entities].sort((a, b) => {
      const aValue = sortValueGetters[sortBy](a);
      const bValue = sortValueGetters[sortBy](b);

      return sortDirection === "asc"
        ? aValue.localeCompare(bValue)
        : bValue.localeCompare(aValue);
    });
  }, [entities, sortBy, sortDirection, profiles, licensesByEmail]);

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    onSelectAll(event.target.checked);
  };

  const handleSelectEntity = (id: string) => {
    onSelectEntity(id);
  };

  if (entities.length === 0) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        py={8}
      >
        <Typography variant="h6" color="text.secondary" gutterBottom>
          No people found
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Add people to start managing analysis entities
        </Typography>
      </Box>
    );
  }

  return (
    <TableContainer component={Paper} variant="outlined">
      <Table size="small">
        <TableHead>
          <TableRow
            sx={{
              backgroundColor: "#f5f5f5",
              "& .MuiTableCell-root": {
                paddingTop: "16px",
                paddingBottom: "16px",
                fontWeight: 600
              }
            }}
          >
            <TableCell padding="checkbox">
              <Checkbox
                indeterminate={
                  selectedIds.length > 0 && selectedIds.length < entities.length
                }
                checked={
                  entities.length > 0 && selectedIds.length === entities.length
                }
                onChange={handleSelectAll}
              />
            </TableCell>
            <TableCell>
              <TableSortLabel
                active={sortBy === "email"}
                direction={sortBy === "email" ? sortDirection : "asc"}
                onClick={() => handleSort("email")}
              >
                Email
              </TableSortLabel>
            </TableCell>
            <TableCell>
              <TableSortLabel
                active={sortBy === "analysis_group"}
                direction={sortBy === "analysis_group" ? sortDirection : "asc"}
                onClick={() => handleSort("analysis_group")}
              >
                Analysis Group
              </TableSortLabel>
            </TableCell>
            <TableCell>
              <TableSortLabel
                active={sortBy === "status"}
                direction={sortBy === "status" ? sortDirection : "asc"}
                onClick={() => handleSort("status")}
              >
                Status
              </TableSortLabel>
            </TableCell>
            {showLicenseColumn && (
              <TableCell>
                <TableSortLabel
                  active={sortBy === "license"}
                  direction={sortBy === "license" ? sortDirection : "asc"}
                  onClick={() => handleSort("license")}
                >
                  License
                </TableSortLabel>
              </TableCell>
            )}
            <TableCell>
              <TableSortLabel
                active={sortBy === "modified"}
                direction={sortBy === "modified" ? sortDirection : "asc"}
                onClick={() => handleSort("modified")}
              >
                Last Modified
              </TableSortLabel>
            </TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedEntities.map((entity) => {
            const isSelected = selectedIds.includes(entity.id);
            const profileName = getProfileName(entity.analysis_profile_id);

            return (
              <TableRow
                key={entity.id}
                hover
                selected={isSelected}
                sx={{
                  "&:last-child td, &:last-child th": { border: 0 },
                  height: 52
                }}
              >
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={isSelected}
                    onChange={() => handleSelectEntity(entity.id)}
                  />
                </TableCell>
                <TableCell>
                  <Box
                    onClick={() => onEditEntity(entity)}
                    sx={{
                      cursor: "pointer",
                      display: "inline-block"
                    }}
                  >
                    {entity.email}
                  </Box>
                </TableCell>
                <TableCell>
                  {profileName ? (
                    <Chip
                      label={profileName}
                      size="small"
                      color="primary"
                      onClick={() => onEditEntity(entity)}
                      sx={{ cursor: "pointer" }}
                    />
                  ) : (
                    <Chip
                      label="Unassigned"
                      size="small"
                      variant="outlined"
                      onClick={() => onEditEntity(entity)}
                      sx={{
                        fontStyle: "italic",
                        cursor: "pointer"
                      }}
                    />
                  )}
                </TableCell>
                <TableCell>
                  <Chip
                    label={entity.status}
                    size="small"
                    color={entity.status === "active" ? "success" : "error"}
                    onClick={() => onEditEntity(entity)}
                    sx={{
                      textTransform: "capitalize",
                      cursor: "pointer"
                    }}
                  />
                </TableCell>
                {showLicenseColumn && (
                  <TableCell>
                    <LicenseCell
                      email={entity.email}
                      licensesByEmail={licensesByEmail}
                      licensesLoading={licensesLoading}
                      licensesFailed={licensesFailed}
                    />
                  </TableCell>
                )}
                <TableCell>
                  <Box
                    onClick={() => onEditEntity(entity)}
                    sx={{
                      cursor: "pointer",
                      display: "inline-block"
                    }}
                  >
                    {new Date(entity.modified).toLocaleString()}
                  </Box>
                </TableCell>
                <TableCell align="right">
                  <IconButton
                    size="small"
                    onClick={() => onEditEntity(entity)}
                    color="primary"
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => onDeleteEntity(entity.id)}
                    color="error"
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
